import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { apiFetch } from '@/lib/api'
import { IconLoader2 } from '@tabler/icons-react'
import { ForumSeo } from '@/components/ForumSeo'
import { useAuthLoading, useConfirmedGuest, useSessionUser } from '@/lib/auth'
import {
  forumAlertError,
  forumAlertOk,
  forumCard,
  forumCardHeader,
  forumField,
  forumKicker,
  forumTextarea,
  forumTitleMd,
  forumLoaderRow,
  forumMutedSm,
} from '@/lib/forumUi'
import { forumPageTitle, truncateForMetaDescription } from '~shared/seo'

const fileInput =
  'w-full cursor-pointer rounded-md border border-zinc-200 bg-white px-2 py-2 text-[13px] file:mr-3 file:rounded-md file:border file:border-zinc-200 file:bg-white file:px-3 file:py-1.5 file:text-[13px] file:font-semibold hover:file:bg-zinc-50'

export function SettingsPage() {
  const me = useSessionUser()
  const authLoading = useAuthLoading()
  const guest = useConfirmedGuest()
  const [displayName, setDisplayName] = useState('')
  const [bio, setBio] = useState('')
  const [status, setStatus] = useState<string | null>(null)

  useEffect(() => {
    if (!me) return
    setDisplayName(me.displayName)

    let alive = true

    void apiFetch<{ user: { displayName: string; bio: string } }>(`/api/users/me`)
      .then(payload => {
        if (!alive) return
        setDisplayName(payload.user.displayName)
        setBio(payload.user.bio ?? '')
      })
      .catch(() => {})

    return () => {
      alive = false
    }
  }, [me])

  if (authLoading) {
    return (
      <div className={forumLoaderRow} aria-busy="true" aria-live="polite">
        <IconLoader2 className="size-5 shrink-0 animate-spin" aria-hidden="true" />
        Checking your session…
      </div>
    )
  }

  if (guest) {
    return (
      <section className={`${forumCard} px-5 py-10 md:px-6`}>
        <ForumSeo
          title={forumPageTitle('Account settings')}
          description={truncateForMetaDescription(
            'Update your NK Our Town avatar, bio, and display preferences.',
          )}
          noindex
        />
        <p className="font-semibold text-zinc-800">Sign in to change profile settings.</p>
        <Link className="mt-5 inline-block" to="/sign-in?next=/settings">
          <Button>Sign in</Button>
        </Link>
      </section>
    )
  }

  if (!me) return null

  return (
    <main className={`mx-auto max-w-2xl ${forumCard}`}>
      <ForumSeo
        title={forumPageTitle('Account settings')}
        description={truncateForMetaDescription(
          'Update your NK Our Town avatar, bio, and display preferences.',
        )}
        noindex
      />
      <header className={forumCardHeader}>
        <p className={forumKicker}>Account</p>
        <h1 className={forumTitleMd}>Settings</h1>
      </header>

      <div className="space-y-5 px-5 py-5 md:px-6 md:py-6">
        {status ? (
          <div
            role="status"
            className={
              status.startsWith('Upload failed') || status === 'Could not save.'
                ? forumAlertError
                : forumAlertOk
            }
          >
            {status}
          </div>
        ) : null}

        <section aria-labelledby="avatar-heading" className="overflow-hidden rounded-xl border border-sky-200/80 bg-sky-50/50 p-4">
          <h2 id="avatar-heading" className="text-sm font-semibold text-zinc-900">
            Avatar (upload)
          </h2>
          <p className={`${forumMutedSm} mt-1 text-zinc-600`}>PNG, JPEG, WebP, or GIF up to ~6 MB. Square crops look cleanest.</p>

          <div className="mt-4 border-t border-sky-200/60 pt-4">
            <label className="mb-2 block text-[13px] font-semibold text-zinc-800" htmlFor="settings-avatar">
              Choose file
            </label>
            <input
              id="settings-avatar"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className={fileInput}
              onChange={async evt => {
                const file = evt.target.files?.[0]
                if (!file) return

                try {
                  setStatus(null)
                  const fd = new FormData()
                  fd.set('file', file)
                  fd.set('kind', 'avatar')

                  await apiFetch<{ asset: { url: string } }>(`/api/upload`, {
                    method: 'POST',
                    form: fd,
                  })

                  setStatus('Avatar updated. Reloading…')
                  evt.target.value = ''
                  window.location.reload()
                } catch {
                  setStatus('Upload failed. Check format and size.')
                }
              }}
            />
          </div>
        </section>

        <form
          className="space-y-4 rounded-xl border border-zinc-200 bg-zinc-50/40 p-4"
          aria-labelledby="profile-form-heading"
          onSubmit={async evt => {
            evt.preventDefault()
            try {
              setStatus(null)
              await apiFetch(`/api/users/me`, {
                method: 'PATCH',
                json: { displayName, bio },
              })
              setStatus('Saved.')
            } catch {
              setStatus('Could not save.')
            }
          }}
        >
          <h2 id="profile-form-heading" className="sr-only">
            Profile text
          </h2>

          <div>
            <label className="mb-2 block text-[13px] font-semibold text-zinc-800" htmlFor="settings-display">
              Display name
            </label>
            <input id="settings-display" value={displayName} onChange={evt => setDisplayName(evt.target.value)} className={forumField} required />
          </div>

          <div>
            <label className="mb-2 block text-[13px] font-semibold text-zinc-800" htmlFor="settings-bio">
              Bio (400 characters max)
            </label>
            <textarea id="settings-bio" value={bio} onChange={evt => setBio(evt.target.value)} className={forumTextarea} maxLength={400} />
          </div>

          <Button type="submit">Save profile</Button>
        </form>
      </div>
    </main>
  )
}
